import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';

const AgriculturalRentalRates = () => {
  const navigate = useNavigate();
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadPageData();
    incrementViewCount();
  }, []);

  const loadPageData = async () => {
    try {
      const { data, error } = await supabase?.from('seo_pages')?.select('*')?.eq('slug', 'agricultural-land-rental-rates-2025')?.single();

      if (!error && data) {
        setPageData(data);
      }
    } catch (error) {
      console.error('Error loading page data:', error);
    } finally {
      setLoading(false);
    }
  };

  const incrementViewCount = async () => {
    try {
      await supabase?.from('seo_pages')?.update({ view_count: supabase?.rpc('increment_view_count') })?.eq('slug', 'agricultural-land-rental-rates-2025');
    } catch (error) {
      console.error('Error incrementing view count:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header 
          calculationData={null}
          onExportPDF={() => {}}
        />
        <main className="pt-16">
          <div className="max-w-4xl mx-auto px-4 py-12">
            <div className="animate-pulse space-y-4">
              <div className="h-8 bg-muted rounded w-3/4"></div>
              <div className="h-4 bg-muted rounded w-1/2"></div>
              <div className="space-y-2">
                <div className="h-4 bg-muted rounded"></div>
                <div className="h-4 bg-muted rounded w-5/6"></div>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>{pageData?.title || 'Agricultural Land Rental Rates 2025 | AgRentIndex.com'}</title>
        <meta name="description" content={pageData?.meta_description || 'Discover the latest agricultural land rental rates for 2025. Learn how to calculate fair market pricing for farmland rentals with our comprehensive guide.'} />
        <meta name="keywords" content={pageData?.keywords?.join(', ') || 'agricultural land rental rates, farmland rent 2025, cash rent rates'} />
        
        {/* Open Graph */}
        <meta property="og:title" content={pageData?.title} />
        <meta property="og:description" content={pageData?.meta_description} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://agrentindex.com/agricultural-land-rental-rates-2025" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageData?.title} />
        <meta name="twitter:description" content={pageData?.meta_description} />
        
        {/* Schema.org */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": pageData?.title,
            "description": pageData?.meta_description,
            "author": {
              "@type": "Organization",
              "name": "AgRentIndex"
            },
            "publisher": {
              "@type": "Organization",
              "name": "AgRentIndex",
              "logo": {
                "@type": "ImageObject",
                "url": "https://agrentindex.com/logo.png"
              }
            },
            "datePublished": pageData?.created_at,
            "dateModified": pageData?.updated_at
          })}
        </script>
      </Helmet>
      <Header 
        calculationData={null}
        onExportPDF={() => {}}
      />
      <main className="pt-16">
        {/* Hero Section */}
        <div className="bg-gradient-to-b from-primary/5 to-background py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <div className="inline-flex items-center space-x-2 bg-primary/10 text-primary px-3 py-1 rounded-full text-sm font-medium mb-4">
                <Icon name="TrendingUp" size={16} />
                <span>Market Analysis 2025</span>
              </div>
              
              <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-6">
                {pageData?.title || 'Agricultural Land Rental Rates 2025'}
              </h1>
              
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
                {pageData?.meta_description}
              </p>

              <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4">
                <Button
                  onClick={() => navigate('/')}
                  size="lg"
                  iconName="Calculator"
                  iconPosition="left"
                >
                  Calculate Fair Rent Now
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate('/guides/farmland-leasing')}
                  size="lg"
                  iconName="FileText"
                  iconPosition="left"
                >
                  Legal Guide
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {/* Table of Contents */}
          <div className="bg-muted rounded-lg p-6 mb-8">
            <h2 className="text-lg font-semibold mb-4 flex items-center">
              <Icon name="List" size={20} className="mr-2" />
              Table of Contents
            </h2>
            <ul className="space-y-2 text-sm">
              <li><a href="#market-overview" className="text-primary hover:underline">Current Market Overview</a></li>
              <li><a href="#key-factors" className="text-primary hover:underline">Key Factors Influencing Rates</a></li>
              <li><a href="#regional-analysis" className="text-primary hover:underline">Regional Rate Analysis</a></li>
              <li><a href="#rental-structures" className="text-primary hover:underline">Rental Agreement Structures</a></li>
              <li><a href="#calculation-methods" className="text-primary hover:underline">Calculation Methodologies</a></li>
              <li><a href="#technology" className="text-primary hover:underline">Technology Integration</a></li>
            </ul>
          </div>

          {/* Article Content */}
          <div className="prose prose-lg max-w-none">
            {pageData?.content ? (
              <div 
                className="content-container"
                dangerouslySetInnerHTML={{ __html: pageData?.content?.replace(/\n/g, '<br />') }}
              />
            ) : (
              <div className="space-y-8">
                <section id="market-overview">
                  <h2 className="text-2xl font-bold mb-4">Current Market Overview</h2>
                  <p className="text-muted-foreground mb-4">
                    Agricultural land rental rates in 2025 vary significantly by region, crop type, and land quality. 
                    According to recent USDA data and market analysis, average cash rent rates have shown moderate 
                    increases compared to 2024, with some regions experiencing more pronounced changes due to 
                    commodity price fluctuations and input cost variations.
                  </p>
                </section>

                <section id="key-factors">
                  <h2 className="text-2xl font-bold mb-4">Key Factors Influencing Rental Rates</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="bg-card border border-border rounded-lg p-4">
                      <h3 className="font-semibold mb-2 flex items-center">
                        <Icon name="Zap" size={16} className="mr-2 text-primary" />
                        Soil Quality & Productivity
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        Prime agricultural land with high corn suitability ratings (CSR2) commands premium rental rates, 
                        often 20-30% above average county rates.
                      </p>
                    </div>
                    <div className="bg-card border border-border rounded-lg p-4">
                      <h3 className="font-semibold mb-2 flex items-center">
                        <Icon name="TrendingUp" size={16} className="mr-2 text-primary" />
                        Commodity Prices
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        The relationship between commodity prices and land rental rates remains strong, 
                        with rates adjusting to price fluctuations with a 6-12 month lag.
                      </p>
                    </div>
                  </div>
                </section>
              </div>
            )}
          </div>

          {/* CTA Section */}
          <div className="bg-primary/5 rounded-xl p-8 mt-12 text-center">
            <h2 className="text-2xl font-bold mb-4">Get Your Fair Rent Analysis</h2>
            <p className="text-muted-foreground mb-6">
              Use our AI-powered calculator to determine fair rental rates based on current market data and your specific land characteristics.
            </p>
            <Button
              onClick={() => navigate('/')}
              size="lg"
              iconName="Calculator"
              iconPosition="left"
            >
              Calculate Fair Rent Now
            </Button>
          </div>

          {/* Related Articles */}
          <div className="mt-12 pt-8 border-t border-border">
            <h2 className="text-xl font-semibold mb-6">Related Resources</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div 
                className="bg-card border border-border rounded-lg p-6 cursor-pointer hover:shadow-lg transition-shadow"
                onClick={() => navigate('/guides/farmland-leasing')}
              >
                <h3 className="font-semibold mb-2">Farmland Leasing Contracts Legal Guide</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Essential legal considerations for farmland lease agreements, covering landlord and tenant responsibilities.
                </p>
                <div className="flex items-center text-primary text-sm">
                  <span>Read More</span>
                  <Icon name="ArrowRight" size={16} className="ml-1" />
                </div>
              </div>
              <div 
                className="bg-card border border-border rounded-lg p-6 cursor-pointer hover:shadow-lg transition-shadow"
                onClick={() => navigate('/results-dashboard')}
              >
                <h3 className="font-semibold mb-2">Market Analysis Dashboard</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  View comprehensive market data, trends, and analytics for agricultural land rental rates.
                </p>
                <div className="flex items-center text-primary text-sm">
                  <span>View Dashboard</span>
                  <Icon name="ArrowRight" size={16} className="ml-1" />
                </div>
              </div>
            </div>
          </div>
        </article>
      </main>
      {/* Footer */}
      <footer className="bg-muted mt-16">
        <div className="max-w-7xl mx-auto px-4 py-12">
          <div className="text-center">
            <div className="flex items-center justify-center space-x-2 mb-4">
              <Icon name="Wheat" size={20} className="text-primary" />
              <span className="text-lg font-semibold">AgRentIndex.com</span>
            </div>
            <p className="text-sm text-muted-foreground">
              © {new Date()?.getFullYear()} AgRentIndex Agricultural Analytics. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default AgriculturalRentalRates;
